import { fetchWrapperPublic } from "helpers/fetch-wrapper";
import { API_BASE_URL } from "helpers/constants";
import { useUserData } from "store/user-data";

const API = (API_BASE_URL || "").replace(/\/$/, "");

export const getIdentifierFromSrc = (src: string): string | null => {
    try {
        const u = new URL(src);
        const siteId = u.searchParams.get("siteId") || u.searchParams.get("tenant");
        if (siteId) return siteId;
        const host = u.hostname;
        return host || null;
    } catch (err) {
        return null;
    }
};

export const validatePayload = (payload: any) => {
    const errors: Record<string, string> = {};
    if (payload.branding && typeof payload.branding !== "object")
        errors.branding = "branding must be an object";
    if (payload.info && typeof payload.info !== "object")
        errors.info = "info must be an object";
    if (payload.content && typeof payload.content !== "object")
        errors.content = "content must be an object";
    return errors;
};

export const loadSiteConfigRequest = async (identifier: string) => {
    const url = API ? `${API}/data/business/site-config/${identifier}/` : `/data/business/site-config/${identifier}/`;
    const res = await fetchWrapperPublic.get(url);
    if (res.status === 404) {
        throw new Error("Site not found (404)");
    }
    if (!res.ok) {
        throw new Error(`Error loading config: ${res.status}`);
    }
    const data = await res.json();
    return data;
};

export const saveSiteConfigRequest = async (identifier: string, payload: any) => {
    const user = useUserData.getState().userData;
    if (!user || !user.is_business_owner) {
        throw new Error("Only business owners can save site configurations.");
    }
    const url = API ? `${API}/data/business/site-config/${user.business_id}/save/` : `/data/business/site-config/${user.business_id}/save/`;
    const res = await fetchWrapperPublic.post(url, payload);

    if (res.status === 403) {
        throw new Error("Forbidden: insufficient permissions (403)");
    }
    if (res.status === 404) {
        throw new Error("Site not found (404)");
    }

    const data = await res.json();
    if (!res.ok) {
        const error: any = new Error("Validation failed");
        error.validationErrors = data;
        throw error;
    }

    return data;
};

// fetch() rejects with an opaque "Failed to fetch" when the connection drops or times out.
const postUpload = async (url: string, formData: FormData) => {
    try {
        return await fetchWrapperPublic.post(url, formData);
    } catch (err) {
        throw new Error("Could not reach the server. Please check your connection and try again.");
    }
};

export const uploadBusinessLogoRequest = async (identifier: string, file: File) => {
    const user = useUserData.getState().userData;
    if (!user || !user.is_business_owner) {
        throw new Error("Only business owners can upload logos.");
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
        throw new Error("File size exceeds 5MB limit");
    }

    const formData = new FormData();
    formData.append('file', file);

    const url = API ? `${API}/data/business/site-config/${identifier}/upload-logo/` : `/data/business/site-config/${identifier}/upload-logo/`;
    const res = await postUpload(url, formData);

    if (res.status === 403) {
        throw new Error("Forbidden: insufficient permissions (403)");
    }
    if (res.status === 404) {
        throw new Error("Business not found (404)");
    }
    if (res.status === 413) {
        throw new Error("That image is too large to upload. Try a smaller one.");
    }

    const data = await res.json();
    if (!res.ok) {
        throw new Error(data.message || "Upload failed");
    }

    return data; // Returns { logo_url, favicon_url }
};

export const uploadBusinessBannerRequest = async (identifier: string, file: File) => {
    const user = useUserData.getState().userData;
    if (!user || !user.is_business_owner) {
        throw new Error("Only business owners can upload banners.");
    }
    if (file.size > 5 * 1024 * 1024) {
        throw new Error("File size exceeds 5MB limit");
    }

    const formData = new FormData();
    formData.append('file', file);

    const url = API ? `${API}/data/business/site-config/${identifier}/upload-banner/` : `/data/business/site-config/${identifier}/upload-banner/`;
    const res = await postUpload(url, formData);
    if (res.status === 413) {
        throw new Error("That image is too large to upload. Try a smaller one.");
    }

    const data = await res.json();
    if (!res.ok) {
        throw new Error(data.message || "Upload failed");
    }

    return data as { url: string };
};
